import PropTypes from 'prop-types'
import clsx from 'clsx'

import CourseStanding from '../../common/CourseStanding'

import styles from './style.module.scss'

/**
 * Wie weit das Team in der Lernstrecke ist.
 *
 * Das Quiz macht jede:r für sich — abgeschlossen wird das Konzept aber
 * gemeinsam, und erst, wenn **alle** zehn Kapitel bestanden haben. Wer das
 * nicht sieht, erfährt vom Rückstand der anderen erst in dem Moment, in dem
 * die Aktivierung oder der erste Bericht daran hängen bleibt.
 *
 * Drei Zustände je Kapitel, und der dritte ist der wichtige: Ein Kapitel, das
 * noch nie abgegeben wurde, sieht anders aus als eines unter der Hürde. „Noch
 * nicht begonnen" und „knapp daneben" sind zwei verschiedene Gespräche.
 *
 * Gezeigt werden Namen und Kapitelstände der eigenen Junior Company — dieselben
 * Angaben, die die Lehrkraft in ihrer Ansicht sieht. Die Antworten selbst
 * stehen hier nicht.
 */
export default function TeamProgress({ students, config, eigeneId, state }) {
	const kapitel = config?.chapters || 10
	const quizPass = config?.quizPass || 4

	if (!students?.length) return null

	return (
		<section className={styles.team}>
			<h3>Lernstrecke im Team</h3>
			<p className={styles.teamHint}>
				Das Quiz macht jede:r für sich: {kapitel} Kapitel, bestanden ab {quizPass} richtigen Antworten. Ein
				leeres Kästchen heißt, dieses Kapitel wurde noch nicht abgegeben. Wie viele von euch abschließen müssen,
				bevor das Konzept zur Bewertung geht, steht darunter.
			</p>

			{/*
			 * Wie viele nötig sind, sagt die Einstellung — je Unternehmensform.
			 * Vorher stand hier „bei allen", und das stimmte nicht mehr, sobald
			 * Junior Achievement Austria eine Quote einstellte.
			 */}
			<CourseStanding state={state} students={students} config={config} ohneListe />

			<ul className={styles.teamList}>
				{students.map(student => {
					const offen = !student.exempt && student.chaptersPassed < kapitel
					const nichts = (student.chapters || []).length === 0

					return (
						<li
							key={student.id}
							className={clsx(styles.teamRow, student.id === eigeneId && styles.teamRowSelf)}
						>
							<span className={styles.teamName}>
								{student.name}
								{student.id === eigeneId && <i className={styles.teamYou}>du</i>}
							</span>

							<span
								className={clsx(
									styles.teamState,
									student.exempt && styles.teamStateExempt,
									!student.exempt && !offen && styles.teamStateDone,
									offen && nichts && styles.teamStateNone
								)}
							>
								{student.exempt
									? 'von der Quizpflicht befreit'
									: nichts
									? 'noch nicht begonnen'
									: `${student.chaptersPassed} von ${kapitel} bestanden`}
							</span>

							<span className={styles.teamChapters}>
								{Array.from({ length: kapitel }, (_, index) => index + 1).map(no => {
									const stand = (student.chapters || []).find(eintrag => eintrag.chapter === no)

									return (
										<i
											key={no}
											title={`Kapitel ${no}`}
											className={clsx(
												styles.teamChapter,
												stand?.passed && styles.teamChapterPassed,
												stand && !stand.passed && styles.teamChapterOpen
											)}
										>
											{stand ? stand.correct : '–'}
										</i>
									)
								})}
							</span>
						</li>
					)
				})}
			</ul>
		</section>
	)
}

TeamProgress.propTypes = {
	state: PropTypes.object,
	students: PropTypes.array,
	config: PropTypes.object,
	eigeneId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
}
